import { HttpTypes } from '@medusajs/types'
import { optionsAsKeymap } from './options-as-keymap'

type StoreProduct = HttpTypes.StoreProduct
type StoreProductVariant = HttpTypes.StoreProductVariant

export const isVariantInStock = (variant: StoreProductVariant): boolean => {
  if (variant.allow_backorder) return true
  if (!variant.manage_inventory) return true
  return (variant.inventory_quantity ?? 0) > 0
}

export const getBestDiscountVariant = (
  product: StoreProduct,
  variants: StoreProductVariant[] = product.variants ?? [],
): StoreProductVariant | null => {
  let bestVariant: StoreProductVariant | null = null
  let bestPercentage = 0

  for (const variant of variants) {
    const calculated = variant.calculated_price?.calculated_amount
    const original = variant.calculated_price?.original_amount

    if (calculated == null || original == null || original <= calculated) continue

    const percentage = (original - calculated) / original

    if (percentage > bestPercentage) {
      bestPercentage = percentage
      bestVariant = variant
    }
  }

  return bestVariant
}

// Variante dont chaque option vaut sa première valeur ; à défaut, la première variante.
const getFirstOptionsVariant = (product: StoreProduct): StoreProductVariant | null => {
  const variants = product.variants ?? []
  const options = product.options ?? []

  if (variants.length === 0) return null

  const defaultOptions = options.reduce<Record<string, string>>((acc, option) => {
    const firstValue = option.values?.[0]?.value
    if (option.id && firstValue) acc[option.id] = firstValue
    return acc
  }, {})

  const matchedVariant = variants.find((variant) => {
    const variantOptions = optionsAsKeymap(variant.options)
    return options.every((option) => !option.id || variantOptions[option.id] === defaultOptions[option.id])
  })

  return matchedVariant ?? variants[0] ?? null
}

// Variante par défaut : la première option si elle a du stock, sinon la première variante
// suivante (dans l'ordre des options du produit) qui en a. Si plus rien n'est en stock,
// on retombe sur la première option pour que la fiche reste cohérente.
export const getDefaultVariant = (product: StoreProduct): StoreProductVariant | null => {
  const firstVariant = getFirstOptionsVariant(product)
  if (!firstVariant || isVariantInStock(firstVariant)) return firstVariant

  const options = product.options ?? []
  const valueRank = (variant: StoreProductVariant): number[] => {
    const variantOptions = optionsAsKeymap(variant.options)
    return options.map((option) => {
      const index = option.values?.findIndex((value) => value.value === variantOptions[option.id])
      return index != null && index >= 0 ? index : Number.MAX_SAFE_INTEGER
    })
  }

  const inStockVariants = (product.variants ?? [])
    .filter(isVariantInStock)
    .map((variant) => ({ variant, rank: valueRank(variant) }))
    .sort((a, b) => {
      for (let i = 0; i < a.rank.length; i++) {
        const diff = (a.rank[i] ?? 0) - (b.rank[i] ?? 0)
        if (diff !== 0) return diff
      }
      return 0
    })

  return inStockVariants[0]?.variant ?? firstVariant
}

// La variante en promo est privilégiée, mais uniquement parmi celles qui ont du stock :
// on ne veut pas présélectionner une option épuisée tant qu'une autre est disponible.
export const getPreselectedVariant = (product: StoreProduct): StoreProductVariant | null => {
  const inStockVariants = (product.variants ?? []).filter(isVariantInStock)

  return getBestDiscountVariant(product, inStockVariants) ?? getDefaultVariant(product)
}
